import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Receipt, Camera, Wallet, FileSpreadsheet, Sparkles, Zap, ArrowRight } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ComparisonTable from "@/components/ComparisonTable";
import GuaranteeBadge from "@/components/GuaranteeBadge";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

export default function Produit() {
  const { t } = useTranslation();
  usePageMeta("product");

  const features = [
    { icon: Receipt, key: "vat", link: "/tarifs" },
    { icon: Camera, key: "ocr" },
    { icon: Wallet, key: "cash" },
    { icon: FileSpreadsheet, key: "fiduciary" },
    { icon: Sparkles, key: "ai" },
    { icon: Zap, key: "voice" },
  ];

  const steps = [1, 2, 3].map((n) => ({ n, title: t(`steps.s${n}_title`), desc: t(`steps.s${n}_desc`) }));

  return (
    <div className="min-h-screen bg-bg text-[var(--text)]">
      <PublicHeader />

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-10 text-center">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-c text-xs font-semibold text-primary-c shadow-sm"
        >
          {t("product_page.badge")}
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.05] tracking-tight max-w-3xl mx-auto"
        >
          {t("product_page.title_1")} <span className="font-serif-italic text-primary-c">{t("product_page.title_accent")}</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-5 text-base sm:text-lg text-text-secondary max-w-2xl mx-auto"
        >
          {t("product_page.subtitle")}
        </motion.p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/register" data-testid="product-cta-register" className="btn-primary inline-flex items-center justify-center gap-2 group">
            {t("hero.cta_primary")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/reserver" data-testid="product-cta-book" className="btn-secondary inline-flex items-center justify-center gap-2">
            {t("book.cta")}
          </Link>
        </div>
      </section>

      {/* 3 Steps */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl md:text-5xl text-center leading-tight">
          {t("steps.title")} <span className="font-serif-italic text-primary-c">{t("steps.title_accent")}</span>.
        </motion.h2>
        <motion.p {...fadeUp} className="text-text-secondary text-center mt-3 max-w-xl mx-auto">{t("steps.sub")}</motion.p>
        <div className="grid md:grid-cols-3 gap-8 sm:gap-10 mt-12">
          {steps.map((it) => (
            <motion.div key={it.n} {...fadeUp} className="relative pl-14">
              <div className="absolute left-0 top-0 w-10 h-10 rounded-2xl bg-primary-c text-white font-display text-lg flex items-center justify-center shadow-lg">{it.n}</div>
              <h3 className="font-display text-xl">{it.title}</h3>
              <p className="text-sm text-text-secondary mt-2 leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 border-t border-c">
        <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight max-w-3xl">
          {t("features.title")} <span className="font-serif-italic text-secondary-c">{t("features.title_accent")}</span>
        </motion.h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-8 sm:mt-10">
          {features.map((f) => (
            <motion.div
              key={f.key}
              {...fadeUp}
              whileHover={{ y: -4 }}
              className="card-c relative"
              data-testid={`product-feature-${f.key}`}
            >
              <f.icon className="w-6 h-6 text-primary-c" strokeWidth={1.7} />
              <h3 className="mt-3 font-semibold text-lg">{t(`features.${f.key}_title`)}</h3>
              <p className="text-sm text-text-secondary mt-2 leading-relaxed">{t(`features.${f.key}_desc`)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Comparison + Guarantee */}
      <ComparisonTable />
      <GuaranteeBadge />

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <motion.div
          {...fadeUp}
          className="rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #F5EFDE 0%, #E8F1EA 100%)" }}
        >
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight">
            {t("final_cta.title_1")} <span className="font-serif-italic text-primary-c">{t("final_cta.title_accent")}</span> {t("final_cta.title_2")}
          </h2>
          <p className="mt-4 text-text-secondary max-w-xl mx-auto text-sm sm:text-base">{t("final_cta.sub")}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="btn-primary inline-flex items-center justify-center gap-2 group">
              {t("final_cta.primary")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/tarifs" className="btn-secondary inline-flex items-center justify-center gap-2">
              {t("public_nav.see_pricing")} →
            </Link>
          </div>
        </motion.div>
      </section>

      <PublicFooter />
    </div>
  );
}
